/**
 * Graph Stage — Stage 2: LDG/prerequisite validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.3
 *
 * Validates:
 * - Family exists in the LDG
 * - Question tier matches LDG node tier
 * - Prerequisite chain is valid (no orphaned families)
 *
 * Note: Most LDG validation happens at graph build time (dagValidator).
 * This stage validates individual questions against the built graph.
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { coach, info } from '../issueContract'
import { isPolicyFeatureEnabled } from '../policyEngine'
import type { LDGraph } from '../../ldg/types'
import type { Question } from '../../../content'

const STAGE: PipelineStage = 2

/**
 * Run graph validation on a question.
 * Requires a built LDG graph instance.
 */
export function runGraphStage(
  question: Question,
  graph?: LDGraph,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!isPolicyFeatureEnabled('ldgValidation')) return issues
  if (!graph || !question.familyId) return issues

  const node = graph.nodes.get(question.familyId)

  if (!node) {
    issues.push(
      info(
        'graph',
        STAGE,
        'GRAPH_FAMILY_NOT_IN_LDG',
        `Family "${question.familyId}" not found in the Learning Dependency Graph`,
        {
          field: 'familyId',
          questionId: qId,
          familyId: fId,
          severity: 15,
        },
      ),
    )
    return issues
  }

  // ── Tier consistency ─────────────────────────────────────────────────────

  if (question.tier !== undefined && question.tier !== node.tier) {
    issues.push(
      coach(
        'graph',
        STAGE,
        'GRAPH_TIER_MISMATCH',
        `Question tier ${question.tier} does not match LDG node tier ${node.tier}`,
        {
          field: 'tier',
          expected: String(node.tier),
          actual: String(question.tier),
          questionId: qId,
          familyId: fId,
          severity: 50,
        },
      ),
    )
  }

  // ── Content availability ─────────────────────────────────────────────────

  if (!node.hasContent) {
    issues.push(
      info(
        'graph',
        STAGE,
        'GRAPH_NO_CONTENT',
        `LDG node "${question.familyId}" is marked as having no content`,
        {
          questionId: qId,
          familyId: fId,
          severity: 10,
        },
      ),
    )
  }

  // ── Prerequisite depth info ──────────────────────────────────────────────

  if (node.depth > 8) {
    issues.push(
      info(
        'graph',
        STAGE,
        'GRAPH_DEEP_PREREQUISITE_CHAIN',
        `Family "${question.familyId}" has prerequisite depth ${node.depth} (deep chain)`,
        {
          questionId: qId,
          familyId: fId,
          severity: 5,
        },
      ),
    )
  }

  return issues
}
